<template>
  <button
    type="button"
    :class="`btn ${fullWidth ? 'w-100' : ''}` + addClass"
    :style="cssString"
    :disabled="
      disabled ||
      loading ||
      (dependency !== 'full'
        ? hasSomeOfDependencies
        : hasUnresolvedDependencies)
    "
    @click="clicked"
  >
    <div class="buttonText" :style="textCssString" v-if="!loading">
      {{ text }}
    </div>
    <div
      v-else
      class="spinner-border spinner-border-sm"
      :style="textCssString"
      role="status"
    >
      <span class="sr-only">Loading...</span>
    </div>
  </button>
</template>

<script>
export default {
  name: "button-component",
  data() {
    return {
      loading: false,
      cssString: "",
      textCssString: ""
    };
  },
  props: {
    requires: {
      type: Array
    },
    grid: {
      type: Object,
      required: false,
      default: () => {}
    },
    backgroundColor: {
      type: String,
      required: false,
      default: "#000"
    },
    borderColor: {
      type: String,
      required: false,
      default: "#000"
    },
    textColor: {
      required: false,
      type: String,
      default: "#fff"
    },
    text: {
      required: false,
      type: String,
      default: "Button"
    },
    borderRadius: {
      required: false,
      type: String,
      default: "0"
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    },
    enabledOn: {
      required: false,
      type: String,
      default: ""
    },
    fullWidth: {
      type: Boolean
    },
    action: {
      type: [Object, Array]
    },
    styles: {
      type: Object,
      default: () => {
        return {};
      }
    },
    textStyles: {
      type: Object,
      default: () => {
        return {};
      }
    },
    dependency: {
      type: String,
      default: "full"
    },
    addClass: {
      type: String
    },
    method: {
      type: String
    }
  },
  watch: {
    styles: {
      immediate: true,
      handler() {
        const keys = Object.keys(this.styles);
        const vals = Object.values(this.styles);
        keys.forEach((key, idx) => (this.cssString += key + ":" + vals[idx]));
        const txtkeys = Object.keys(this.textStyles);
        const txtvals = Object.values(this.textStyles);
        txtkeys.forEach(
          (key, idx) => (this.textCssString += key + ":" + txtvals[idx])
        );
      }
    },
    enabledOn: {
      immediate: true,
      handler(val) {
        if (!val || val == "") {
          return;
        }
        return this.$store.state[val];
      }
    }
  },
  computed: {
    hasUnresolvedDependencies() {
      return this.$store.getters.hasUnresolvedDependencies(this.requires);
    },
    hasSomeOfDependencies() {
      return this.$store.getters.hasSomeOfDependencies(this.requires);
    }
  },
  methods: {
    async clicked() {
      if (this.method) {
        this.$eventHub.$emit(this.method);
        return;
      }

      if (!this.action) return;

      if (this.action.anchor) {
        return this.action.targetBlank
          ? window.open(this.action.link, "_blank")
          : (window.location.href = this.action.link);
      }
      if (this.action.length > 0) {
        // console.log("inside actions loop::");
        for (let i = 0; i < this.action.length; i++) {
          const act = this.action[i];
          const fn = act.fn;
          const params = act.params;
          await this[fn](params, this);
        }
        return;
      }
      const fn = this.action.fn;
      const params = this.action.params;
      await this[fn](params, this);
      // const params = this.action.params.toString();
      // eval(`this.${this.action.fn}(params)`);
    }
  }
};
</script>

<style lang="scss" scoped>
.btn {
  font-family: "Plus Jakarta Sans";

  &:focus,
  &:active {
    outline: none !important;
    box-shadow: none;
  }
}

.btn {
  font-family: "Plus Jakarta Sans";
  color: #ffffff;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 18px;
  line-height: 23px;
  padding: 10px 30px;
  border-radius: 4px;
  font-weight: 700;
  white-space: nowrap;
  max-width: 270px;
  height: 50px;
  background: linear-gradient(265.49deg, #007ac2 0%, #0166a2 99.55%),
    linear-gradient(84.21deg, #050e23 1.83%, #0a1b43 99.25%),
    linear-gradient(0deg, #050e23, #050e23),
    linear-gradient(265.49deg, #007ac2 0%, #0166a2 99.55%);

  &:hover,
  &:focus {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.16) !important;
  }

  // &:active {
  //   border-color: #deba06;
  // }

  &-min {
    @media (max-width: 574px) {
      font-size: 17px;
    }
  }

  &-white {
    border-color: var(--brand-color);
    color: var(--brand-color);
    background-color: #fff;
  }

  &-transparent {
    max-width: 350px;
    border-color: #424242;
    color: #424242;
    &:hover,
    &focus {
      background-color: #424242;
      color: #ffffff;
    }
  }

  &-yellow {
    color: #fff !important;
    border: 0 !important;
    background: linear-gradient(
      57.51deg,
      #f2ac29 3.98%,
      #f6c05b 95.58%
    ) !important;
  }

  &-blue-receipt-upload {
    background: #3A86CA !important;
    color: #ffffff !important;
    &:hover,
    &focus {
      background: #FFFFFF !important;
      border: 2px solid #3074B1;
      box-sizing: border-box;
      color: #3074B1 !important;
    };
    &[disabled],
    &:disabled{
      background: #BCD0E1 !important;
      border-radius: 8px;
      color: #ffffff !important
    };
  }

  &-cyan {
    background: linear-gradient(265.49deg, #00B6C2 0%, #009CA6 99.55%);
    &:hover,
    &focus {
      background:linear-gradient(265.49deg, #009CA6 0%, #00B6C2 99.55%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &[disabled],
    &:disabled{
      background: #A2B6B8 !important;
    };
    &:active{
      background: linear-gradient(265.49deg, #009CA6 0%, #00B6C2 99.55%);
      color: #ffffff;
    }
  }

  &-cyan-secondary {
    background: linear-gradient(57.51deg, #00B6C2 3.98%, #57D2DA 95.58%) !important;
    border: 0 !important;
    border-radius: 8px;    
    color: #fff !important;    
    &:active{
      background: #fff !important;
      color: #57D2DA !important;
      border: 1px solid #57D2DA !important;
    }
  }

  &-mint{
    border-radius: 8px;
    background: #00B398;
    &:hover,
    &focus {
      background-color: #006647;
      color: #ffffff;
    };
    &[disabled],
    &:disabled{
      background-color: #A3C2B9 !important;
    };
    &:active{
      background: #FFFFFF;
      border: 1px solid #006647;
      box-sizing: border-box;
      color: #006647;
    }
  }
  // background: linear-gradient(265.49deg, #008059 0%, #006647 99.55%);
  // background: #00B398;
  &-mint-green{
    border-radius: 8px;
    background: linear-gradient(265.49deg, #008059 0%, #006647 99.55%)!important;
    color:#fff !important ;
    &:hover,
    &focus {
      background-color: #006647;
      color: #ffffff;
    };
    &[disabled],
    &:disabled{
      background-color: #A3C2B9 !important;
    };
    &:active{
      background: #FFFFFF;
      border: 1px solid #006647;
      box-sizing: border-box;
      color: #006647;
    }
  }

  &-purple {
    background: linear-gradient(85.69deg, #8C3D79 3.53%, #AE4C96 99.21%) !important;
    color: #fff !important;
  }

  &-purple-gradient {
    background: linear-gradient(226deg, #8F6DBE 0%, #5D3B8C 100%) !important;
    box-shadow: 0px 16px 32px -3px #D0B3B4;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(226deg, #8F6DBE 0%, #5D3B8C 100%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &:active{
      background: #3E1C6D !important;
    }
    &[disabled],
    &:disabled{
      background: #D4D4D4 !important;
      box-shadow: none !important;
    }
  }

  &-purple-gradient-secondary {
    background: linear-gradient(227deg, #D9308D 0.01%, #B10865 100%) !important;
    box-shadow: 0px 16px 32px -3px #D0B3B4;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(227deg, #D9308D 0.01%, #B10865 100%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &:active{
      border: 1px solid #B10865 !important;
      background: #fff !important;
      color: #B10865 !important;
      transition: 5000;
    }
    &[disabled],
    &:disabled{
      background: #D4D4D4 !important;
      box-shadow: none !important;
    }
  }

  &-dark-green {
    box-shadow: 0px 16px 32px -3px #CCE0D9;
    border-radius: 8px;
    background: linear-gradient(265.18deg, #0A745C 0.01%, #00B098 100%) !important;
    color: #fff !important;
    border: 0 !important;
    transition: 5000;
    &:active{
        background: #0A8870 !important;
    }
    &:hover,
      &focus {
      background: linear-gradient(265.18deg, #00B098 0.01%, #0A745C 100%) !important;
      color: #ffffff;
    };
    &[disabled],
      &:disabled{
        background: #ADB7B4 !important;
        box-shadow: none !important;
      }
  }
  &-dark-red-gradient {
      background: linear-gradient(57.51deg, #9E1C20 3.98%, #E31F26 95.58%) !important;
      box-shadow: 0px 16px 32px -3px #FBDEDF;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(77.91deg, #E31F26 12.98%, #9E1C20 81.31%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #9E1C20 !important;
      }
      &[disabled],
      &:disabled{
        background: #D4D0D1 !important;
        box-shadow: none !important;
      }
  }
  &-dark-red-gradient2 {
        background: linear-gradient(181.37deg, #DC0028 -14.79%, #A50001 125.74%) !important;
        box-shadow: 0px 16px 32px -3px #FBDEDF;
        color: #fff !important;
        border: 0 !important;
        &:hover,
        &focus {
          background: linear-gradient(77.91deg, #A50001 12.98%, #DC0028 81.31%) !important;
          color: #ffffff;
          transition: 5000;
        };
        &:active{
            background: #9E1C20 !important;
        }
        &[disabled],
        &:disabled{
          background: #D4D0D1 !important;
          box-shadow: none !important;
        }
  }

  &-burgundy-gradient {
      background: radial-gradient(92.42% 226.04% at 50% 50.57%, #6B3050 0%, #E31F26 56.77%, #A26971 100%) !important;
      box-shadow: 0px 16px 32px -3px #D0B3B4;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: radial-gradient(205.76% 574.62% at 50% 50.57%, #6B3050 0%, #E31F26 56.77%, #A26971 100%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #6B3050 !important;
      }
      &[disabled],
      &:disabled{
        background: #A7AFAD !important;
        box-shadow: none !important;
      }
  }

  &-burgundy-gradient-secondary {
      background: linear-gradient(81.86deg, #6B3050 7.41%, #E31F26 221.06%) !important;
      box-shadow: 0px 16px 32px -3px #FBDEDF;
      border: 1px solid #6B3050 !important;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(84.52deg, #E31F26 -43.92%, #6B3050 97.81%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #FFFFFF !important;
          color: #6B3050 !important;
      }
      &[disabled],
      &:disabled{
        background: #D4D0D1 !important;
        box-shadow: none !important;
      }
  }

  &-dark-red-radial {
      background: radial-gradient(92.42% 226.04% at 50% 50.57%, #E31F26 0%, #561617 100%)  !important;
      box-shadow: 0px 16px 32px -3px #D0B3B4;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: radial-gradient(176.06% 684.89% at 50% 50.57%, #E31F26 0%, #561617 100%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #561617 !important;
      }
      &[disabled],
      &:disabled{
        background: #AFA7A7 !important;
        box-shadow: none !important;
      }
  }

  &-dark-red-radial-secondary {
      background: linear-gradient(81.86deg, #561617 7.41%, #E31F26 221.06%) !important;
      box-shadow: 0px 16px 32px -3px #FBDEDF;
      border: 1px solid #6B3050 !important;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), linear-gradient(84.52deg, #E31F26 -43.92%, #561617 97.81%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          border: 1px solid #E31F26 !important;
          background: #FFFFFF !important;
          color: #561617 !important;
      }
      &[disabled],
      &:disabled{
        background: #D4D0D1 !important;
        box-shadow: none !important;
      }
  }

  &-blue-gradient {
    background: linear-gradient(263.62deg, #27A6DE -15.05%, #2D3380 159.56%) !important;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(85.17deg, #2D3380 -10.46%, #27A6DE 108.32%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #FFFFFF !important;
          border: 1px solid #27A6DE !important;
        .buttonText {
          background: -webkit-linear-gradient(263.62deg, #27A6DE -15.05%, #2D3380 159.56%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
        }
      }
      &[disabled],
      &:disabled{
        background: #D4D0D1 !important;
        box-shadow: none !important;
      }
  }

  &-dark-blue-gradient {
    box-shadow: 0px 16px 32px -3px #D4E1F1;
    border-radius: 8px;
    background: linear-gradient(265.18deg, #00AEEF 0.01%, #024491 100%) !important;
    color: #fff !important;
    border: 0 !important;
    &:active{
        background: #024491 !important;
    }
    &:hover,
      &focus {
      background: linear-gradient(265.49deg, #024491 0%, #00AEEF 99.55%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &[disabled],
      &:disabled{
        background: #A4AFBC !important;
        box-shadow: none !important;
      }
  }

  &-dark-green-secondary {
    background: linear-gradient(265.59deg, #1E8870 -19.85%, #7BA19A 100%) !important;
    border-radius: 8px;
    color: #fff !important;
    &:hover,
    &focus {
      background: linear-gradient(265.59deg, #7BA19A 100%, #1E8870 -19.85%) !important;
    }
    &:active{
      background: #fff !important;
      color: #7BA19A !important;
      border: 1px solid #7BA19A;
    }
  }

  &-light-blue {
    color: #fff !important;
    border: 0 !important;
    background: linear-gradient(
      57.51deg,
      #5b90d6 3.98%,
      #99bae6 95.58%
    ) !important;
  }

  &-light-blue-gradient {
    border: none !important;
    background: linear-gradient(265.49deg, #0083A0 0%, #5DBED4 99.55%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #C0E7F0;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.49deg, #5DBED4 0%, #0083A0 99.55%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
        background: #0083A0 !important;
      }
      &[disabled],
        &:disabled{
        background: #9CACB1 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }
  &-light-blue-gradient-secondary {
    border: none !important;
    background: linear-gradient(265.49deg, #0083A0 0%, #5DBED4 99.55%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #C0E7F0;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.49deg, #5DBED4 0%, #0083A0 99.55%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
        background: #FFFFFF !important;
        color: #0083A0 !important;
        border: 1px solid #0083A0 !important;
      }
      &[disabled],
        &:disabled{
        background: #9CACB1 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-light-green {
    background: linear-gradient(265.49deg, #5EBFA4 0%, #43A88C 99.55%) !important;
    color: #fff !important;
    border: 0 !important;
  }

  &-olive-green {
    background: #A5C03B !important;
    color: #fff !important;
    border: 0 !important;
  }

  &-olive-green-gradient {
    background: linear-gradient(265.49deg, #A5C03B 0%, #8FA33E 99.55%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #E9F1C8;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.49deg, #8FA33E 0%, #A5C03B 99.55%) !important;
        color: #ffffff;
        transition: 5000;
      };

      &:active{
        background: #778A2B !important;
      }

      &[disabled],
      &:disabled{
        background: #D5D9C6 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-red-gradient {
    border: none !important;
    background: linear-gradient(57.51deg, #9E1C20 3.98%, #E31F26 95.58%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #D0B3B4;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.49deg, #B01911 0%, #E41B12 99.55%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
        background: #9E1C20 !important;
      }
      &[disabled],
        &:disabled{
       background: #AFA7A7 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-red-gradient-secondary {
    border: none !important;
    background: linear-gradient(57.51deg, #9E1C20 3.98%, #E31F26 95.58%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #D0B3B4;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(57.51deg, #9E1C20 3.98%, #E31F26 95.58%) !important;
        transition: 5000;
      };
      &:active{
        background: #FFFFFF !important;
        color: #B01911 !important;
      }
      &[disabled],
        &:disabled{
       background: #AFA7A7 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-green-accent {
    background: linear-gradient(265.49deg, #00A875 0%, #00A875 99.55%) !important;
    color: #fff !important;
    border: 0 !important;
  }

  &-orange {
    color: #fff !important;
    border: 0 !important;
    background: linear-gradient(
      57.51deg,
      #ff8838 3.98%,
      #ff9752 95.58%
    ) !important;
  }

  &-orange-gradient {
    border: none !important;
    background: linear-gradient(265.18deg, #C36235 0.01%, #F27B26 100%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #FAD5C4;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.18deg, #F27B26 100%, #C36235 0.01%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
        background: #B95D32 !important;
      }
      &[disabled],
        &:disabled{
       background: #959595 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-orange-gradient-secondary {
    border: none !important;
    background: linear-gradient(265.18deg, #C36235 0.01%, #F27B26 100%) !important;
    color: #fff !important;
    box-shadow: 0px 16px 32px -3px #FAD5C4;
    border-radius: 8px;
    &:hover,
      &focus {
        background: linear-gradient(265.18deg, #F27B26 100%, #C36235 0.01%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
        background: #FFFFFF !important;
        color: #FF9752 !important;
        border: 1px solid #F27B26 !important;
      }
      &[disabled],
        &:disabled{
       background: #959595 !important;
        color: #fff !important;
        box-shadow: none !important;
      }
  }

  &-mandarin{
    color: #fff;
    border: 0 !important;
    background: linear-gradient(85.57deg, #FE5000 1.42%, #FF7333 99.34%) !important;
    box-shadow: 0px 16px 32px -7px rgba(255, 192, 163, 0.86);
    border-radius: 8px;
      &:hover,
      &focus {
        background: linear-gradient(85.57deg, #FF7333 1.42%, #FE5000 99.34%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &[disabled],
      &:disabled{
        background: #E0C4B8 !important;
        color: #C18971 !important;
      }
  }

  &-link {
    background: transparent !important;
    border-color: transparent !important;
    color: #050e23 !important;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.03em;

    .buttonText::before {
      content: url("~@/assets/icons/arrow.svg");
      white-space: pre;
      display: inline-block;
      margin-right: 10px;
    }

    &:hover,
    &:focus {
      box-shadow: none !important;
      text-decoration: underline;
      text-underline-position: under;
    }
  }
   &-elmex-green-gradient {
      background: linear-gradient(265.18deg, #0A745C 0.01%, #00B098 100%) !important;
      box-shadow: 0px 16px 32px -3px #D0B3B4;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(265.18deg, #00B098 100%, #0A745C 0.01%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #0A8870 !important;
      }
      &[disabled],
      &:disabled{
        background: #A7AFAD !important;
        box-shadow: none !important;
      }
  }

  &-elmex-green-secondary {
      background: linear-gradient(265.59deg, #1E8870 -19.85%, #7BA19A 100%) !important;
      box-shadow: 0px 16px 32px -3px #FBDEDF;
      border: 1px solid #1E8870 !important;
      color: #fff !important;
      border: 0 !important;
      &:hover,
      &focus {
        background: linear-gradient(265.59deg, #7BA19A 100%, #1E8870 -19.85%) !important;
        color: #ffffff;
        transition: 5000;
      };
      &:active{
          background: #FFFFFF !important;
          color: #0A8870 !important;
      }
      &[disabled],
      &:disabled{
        background: #D4D0D1 !important;
        box-shadow: none !important;
      }
  }

  &-pink-gradient{
    background: linear-gradient(265.35deg, #5D3B8C -45.43%, #D9308D 100%) !important;
    box-shadow: 0px 16px 32px -3px #D0B3B4;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(265.35deg, #D9308D -45.43%, #5D3B8C 100%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &:active{
      background: #0A8870 !important;
    }
    &[disabled],
    &:disabled{
      background: #ADB7B4 !important;
      box-shadow: none !important;
    }
  }

  &-pink-gradient-secondary{
    background: linear-gradient(265.59deg, #5D3B8C -19.85%, #9D84BF 100%) !important;
    box-shadow: 0px 16px 32px -3px rgba(93, 59, 140, 0.4);
    border: 1px solid #9D84BF !important;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(87.11deg, #1E8870 -51.33%, #7BA19A 138.2%, #7BA19A 138.2%) !important;
      color: #ffffff;
      transition: 5000;
    };
    &:active{
      background: #FFFFFF !important;
      color: #9D84BF !important;
      border: 1px solid #9D84BF !important;
    }
    &[disabled],
     &:disabled{
      background: #ADB7B4 !important;
      box-shadow: none !important;
     }
  }

  &-ajax-green-gradient{
    background: linear-gradient(265.18deg, #006243 0.01%, #599985 100%) !important;
    box-shadow: 0px 16px 32px -3px #CCE0D9 !important;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(265.18deg, #006243 0.01%, #599985 100%) !important;
      color: #fff !important;
      transition: 5000 !important;
    };
    &:active{
      border: 1px solid #006243 !important;
      background: #006243;
    }
    &[disabled],
     &:disabled{
      background: #ADB7B4 !important;
      box-shadow: none !important;
     }
  }
  &-ajax-pink-gradient{
    background: linear-gradient(265.49deg, #BC4880 0%, #D05C94 99.55%);
    box-shadow: 0px 16px 32px -3px #CCE0D9 !important;
    color: #fff !important;
    border: 0 !important;
    &:hover,
    &focus {
      background: linear-gradient(265.49deg, #BC4880 0%, #D05C94 99.55%) !important;
      color: #fff !important;
      transition: 5000 !important;
    };
    &:active{
      border: 1px solid #BC4880 !important;
      background: #BC4880;
    }
    &[disabled],
     &:disabled{
      background: #ADB7B4 !important;
      box-shadow: none !important;
     }
  }
}
.long-text {
  @media (min-width: 992px) and (max-width: 1350px) {
    .buttonText {
      font-size: 10px !important;
    }
  }
  @media (max-width: 450px) {
    .buttonText {
      font-size: 10px !important;
    }
  }
}

@media (max-width: 768px) {
  .mobileFullWidth {
    width: 500px !important;
  }
}

@media (max-width: 575px) {
  .mobileFullWidth {
    width: 250px !important;
  }

  .mobileWrapper {
    margin-left: 15px;
    margin-right: 15px;
  }
}

@media (max-width: 360px) {
  .mobileFullWidth {
    width: 200px !important;
  }
}

@media (max-width: 300px) {
  .mobileFullWidth {
    width: 160px !important;
  }
}

@media (max-width: 260px) {
  .mobileFullWidth {
    width: 140px !important;
  }
}

@media (max-width: 250px) {
  .mobileFullWidth {
    width: 100px !important;
    font-size: 11px;
    text-align: center;
  }
}
</style>
